import { useEffect, useState, RefObject, DependencyList, useRef, useLayoutEffect } from 'react'
import { debounce } from 'lodash'

export type ScrollLocation = 'top' | 'bottom'

export type ScrollLocationOptions = {
  ref: RefObject<HTMLElement>
  topTreshold?: number
  bottomTreshold?: number
  scrollDebounceWait?: number
}

export function useScroll(ref: RefObject<HTMLElement>, wait = 30) {
  const [scroll, setScroll] = useState<null | number>(null)
  useEffect(() => {
    const { current: element } = ref
    if (!element) {
      return undefined
    }
    const listener = debounce(() => {
      setScroll(element.scrollTop)
    }, wait)
    // @todo add browser check for passive events
    element.addEventListener('scroll', listener, { passive: true })
    return () => {
      element.removeEventListener('scroll', listener)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, wait])

  return scroll
}

export function useScrollLocation({
  ref,
  topTreshold = 0,
  bottomTreshold = 0,
  scrollDebounceWait,
}: ScrollLocationOptions): ScrollLocation[] {
  const [locations, setLocations] = useState<ScrollLocation[]>([])
  const scroll = useScroll(ref, scrollDebounceWait)
  useEffect((): void => {
    const { current: area } = ref
    if (!area || scroll === null) return
    const hitsTop = scroll - topTreshold <= 0
    const hitsBottom = scroll >= area.scrollHeight - area.offsetHeight - bottomTreshold
    const activeLocations: ScrollLocation[] = []
    if (hitsTop) {
      activeLocations.push('top')
    }
    if (hitsBottom) {
      activeLocations.push('bottom')
    }
    setLocations(activeLocations)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, scroll])

  return locations
}

/** How close to an edge still counts as being at it. */
const scrollLockTreshold = 120

/**
 * Follows the target only while the scroll is sitting at the bottom, so a new message
 * scrolls into view for someone watching the end of the feed but not for someone reading
 * further up.
 */
export function useEnableWhileAtBottom(ref: RefObject<HTMLElement>, setEnabled: (enabled: boolean) => void) {
  const scrollLocation = useScrollLocation({
    ref,
    topTreshold: scrollLockTreshold,
    bottomTreshold: scrollLockTreshold,
  })
  const isAtBottom = scrollLocation.includes('bottom')
  useEffect(() => {
    setEnabled(isAtBottom)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAtBottom])
}

/**
 * Flips to true the first time the element is scrolled by hand.
 */
export function useManualScrollTakeover(ref: RefObject<HTMLElement>) {
  const hasScrolledManually = useRef(false)
  useEffect(() => {
    const { current: element } = ref
    if (!element) {
      return undefined
    }
    const takeOver = () => {
      hasScrolledManually.current = true
    }
    const events = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const
    events.forEach((event) => element.addEventListener(event, takeOver, { passive: true }))
    return () => events.forEach((event) => element.removeEventListener(event, takeOver))
  }, [ref])

  return hasScrolledManually
}

/** While true, a hook neither scrolls nor spends its first scroll. */
export type SkippableOptions = { skip?: boolean }

/**
 * Keeps what the reader is looking at in place when items arrive above the viewport, by
 * growing `scrollTop` by however much the content grew since the last `deps` change. It
 * only sees the growth, not where it happened — pass deps that change on a prepend.
 */
export function usePreserveScrollOnPrepend(
  ref: RefObject<HTMLElement>,
  deps: DependencyList,
  { skip = false }: SkippableOptions = {}
) {
  const prevScrollHeight = useRef<number>(0)
  useEffect(() => {
    if (ref.current) {
      prevScrollHeight.current = ref.current.scrollHeight
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  useLayoutEffect(() => {
    const { current: element } = ref
    if (!skip && element && prevScrollHeight.current > 0) {
      element.scrollTop = element.scrollTop + element.scrollHeight - prevScrollHeight.current
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/**
 * Scrolls the target into view whenever `deps` change — instantly the first time, then
 * smoothly. Returns a switch to stop and resume, e.g. once the reader scrolls away.
 */
export function useScrollToTarget(
  targetRef: RefObject<HTMLElement>,
  initiallyEnabled: boolean,
  deps: DependencyList,
  { skip = false }: SkippableOptions = {}
) {
  const [enabled, setEnabled] = useState<boolean>(initiallyEnabled)
  const initialScroll = useRef<boolean>(true)
  useEffect((): void => {
    const { current: target } = targetRef
    if (skip || !enabled || !target || !target.scrollIntoView) return
    if (initialScroll.current) {
      target.scrollIntoView({ behavior: 'auto' })
      initialScroll.current = false
    } else {
      target.scrollIntoView({ behavior: 'smooth' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  return setEnabled
}
